import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { AuctionCard } from "@/components/AuctionCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CalendarClock, Flame, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function UpcomingAuctions() {
  const { user } = useAuth();
  const [data, setData] = useState({ scheduled: [], ending_soon: [] });

  useEffect(() => {
    api.get("/auctions/upcoming").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent border border-amber-500/20 p-6 sm:p-8 mb-10">
          <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold uppercase tracking-wider mb-2">
            <Megaphone className="h-4 w-4" /> Partners & sponsors welcome
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Upcoming auctions</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            A front-row look at what's about to go live and what's closing soon. Perfect for estate sellers, pickers and
            partners who want to promote a big lot before the gavel drops.
          </p>
          <div className="flex gap-3 mt-5">
            <Link to={user ? "/sell" : "/register"}>
              <Button data-testid="upcoming-list-cta" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10 px-5">List an item or estate</Button>
            </Link>
            <a href="mailto:partners@hustlers-hall.com">
              <Button data-testid="upcoming-partner-cta" variant="outline" className="rounded-full h-10 px-5 border-white/15 bg-white/5 text-white hover:bg-white/10">Partner with us</Button>
            </a>
          </div>
        </div>

        <Section icon={CalendarClock} title="Starting soon" testid="upcoming-scheduled" items={data.scheduled}
          empty="No scheduled auctions yet — schedule one when you list to build hype early." />
        <Section icon={Flame} title="Ending soon — don't miss these" testid="upcoming-ending" items={data.ending_soon}
          empty="Nothing closing right now. Check the live auctions." />
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, testid, items, empty }) => (
  <div className="mb-12" data-testid={testid}>
    <div className="flex items-center gap-2 mb-5">
      <Icon className="h-5 w-5 text-amber-400" />
      <h2 className="text-xl font-bold text-white">{title}</h2>
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-slate-500 rounded-xl bg-[#121721] border border-white/10 p-6">{empty}</div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((a, i) => (
          <AuctionCard key={a.auction_id} auction={a} index={i} />
        ))}
      </div>
    )}
  </div>
);
